import React, { useState, useEffect, useCallback } from 'react';
import './DashboardHome.css';
import { useNavigate } from 'react-router-dom';
import { FaChevronRight, FaTachometerAlt } from 'react-icons/fa';
import { FiTrendingUp, FiUsers, FiClock, FiAlertCircle, FiRefreshCw } from 'react-icons/fi';
import { commanServicer } from '../../Commen_Services/common.services';
import customToast from '../Toast/CustomToast';
import { useLoading } from '../../Loader/LoadingContext';
import StatCard from '../StatCard';
import { PageEnums } from '../../../_enums/enum.services';

interface RecentCollectionRow {
    id: string;
    customer: string;
    employee: string;
    amount: number;
    mode: string;
    dateTime: string;
    collectionDate?: string | Date;
}

interface PendingCollectionRow {
    id: string;
    companyName: string;
    date: string;
    amount: number;
    pendingSync: string;
    collectionDate?: string | Date;
}

interface DashboardSummary {
    totalCollection: number;
    cashApprovalPendingAmount: number;
    activeEmployees: number;
    totalEmployees: number;
    inactiveEmployees: number;
    pendingSyncCollectionAmount: number;
    pendingSyncCount: number;
}

interface ApiSummary {
    totalCollection?: number;
    cashApprovalPendingAmount?: number;
    activeEmployees?: number;
    inactiveEmployees?: number;
    totalEmployees?: number;
    pendingSyncCollectionAmount?: number;
    pendingSyncCount?: number;
}

interface ApiRecentCollection {
    customerName?: string;
    employeeName?: string;
    amount?: number;
    modeOfPayment?: string;
    collectionDate?: string | Date;
}

interface ApiPendingCollection {
    companyName?: string;
    collectionDate?: string | Date;
    amount?: number;
    syncStatus?: string;
}

interface DashboardApiData {
    summary?: ApiSummary | ApiSummary[];
    recentCollections?: ApiRecentCollection[];
    pendingCollections?: ApiPendingCollection[];
    dashboardSummary?: ApiSummary[];
    dashboardRecentCollections?: ApiRecentCollection[];
    dashboardPendingCollections?: ApiPendingCollection[];
}

interface DashboardData {
    summary: DashboardSummary;
    recentCollections: RecentCollectionRow[];
    pendingCollections: PendingCollectionRow[];
    totalCollectionDays: number;
}

const emptySummary: DashboardSummary = {
    totalCollection: 0,
    cashApprovalPendingAmount: 0,
    activeEmployees: 0,
    totalEmployees: 0,
    inactiveEmployees: 0,
    pendingSyncCollectionAmount: 0,
    pendingSyncCount: 0,
};

const emptyData: DashboardData = {
    summary: emptySummary,
    recentCollections: [],
    pendingCollections: [],
    totalCollectionDays: 0,
};

const getDefaultDates = (): { dateFrom: string; dateTo: string } => {
    const today = new Date();
    const dateTo = today.toISOString().split('T')[0];
    const lastDate = new Date(today);
    lastDate.setDate(lastDate.getDate() - 30);
    const dateFrom = lastDate.toISOString().split('T')[0];
    return { dateFrom, dateTo };
};

const formatRupees = (num: number): string => `₹ ${Number(num || 0).toLocaleString('en-IN')}`;

const pad = (n: number): string => String(n).padStart(2, '0');

const formatDateParts = (value: string | Date): { day: string; month: string; year: number; hour: number; minute: string; valid: boolean } => {
    if (!value) return { day: '', month: '', year: 0, hour: 0, minute: '', valid: false };

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return { day: '', month: '', year: 0, hour: 0, minute: '', valid: false };
    }

    return {
        day: pad(date.getDate()),
        month: pad(date.getMonth() + 1),
        year: date.getFullYear(),
        hour: date.getHours(),
        minute: pad(date.getMinutes()),
        valid: true,
    };
};

const formatDisplayDateTime = (value: string | Date): string => {
    const parts = formatDateParts(value);
    if (!parts.valid) return '';

    const ampm = parts.hour >= 12 ? 'PM' : 'AM';
    const hour12 = parts.hour % 12 || 12;

    return `${parts.day}-${parts.month}-${parts.year}, ${pad(hour12)}:${parts.minute} ${ampm}`;
};

const formatDisplayDate = (value: string | Date): string => {
    const parts = formatDateParts(value);
    if (!parts.valid) return '';

    return `${parts.day}-${parts.month}-${parts.year}`;
};

const toISODate = (value: string | Date): string => {
    if (!value) return '';

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';

    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

const DashboardHome: React.FC = () => {
    const navigate = useNavigate();
    const { setLoading } = useLoading();
    const defaultDates = getDefaultDates();

    const [data, setData] = useState<DashboardData>(emptyData);
    const [fetchedAtLeastOnce, setFetchedAtLeastOnce] = useState<boolean>(false);

    const transformApiData = (payload: DashboardApiData | any): DashboardData => {
        const dashboardSummaryRows = Array.isArray(payload?.dashboardSummary) ? payload.dashboardSummary : [];
        const recentCollectionRows = Array.isArray(payload?.dashboardRecentCollections) ? payload.dashboardRecentCollections : [];
        const pendingCollectionRows = Array.isArray(payload?.dashboardPendingCollections) ? payload.dashboardPendingCollections : [];

        let summarySource: ApiSummary = {};

        if (payload?.summary) {
            summarySource = Array.isArray(payload.summary) ? payload.summary[0] : payload.summary;
        } else if (dashboardSummaryRows.length > 0) {
            summarySource = dashboardSummaryRows[0] || {};
        } else if (Array.isArray(payload) && payload.length > 0) {
            const firstItem = payload[0];
            summarySource = Array.isArray(firstItem) ? (firstItem[0] || {}) : (firstItem || {});
        } else if (payload && typeof payload === 'object') {
            summarySource = payload;
        }

        const activeEmp = Number(summarySource?.activeEmployees ?? 0);
        const inactiveEmp = Number(summarySource?.inactiveEmployees ?? 0);
        const totalEmployees = Number(summarySource?.totalEmployees ?? (activeEmp + inactiveEmp));

        const summary: DashboardSummary = {
            totalCollection: Number(summarySource?.totalCollection ?? 0),
            cashApprovalPendingAmount: Number(summarySource?.cashApprovalPendingAmount ?? 0),
            activeEmployees: activeEmp,
            inactiveEmployees: inactiveEmp,
            totalEmployees,
            pendingSyncCollectionAmount: Number(summarySource?.pendingSyncCollectionAmount ?? 0),
            pendingSyncCount: Number(summarySource?.pendingSyncCount ?? 0),
        };

        let apiRecent: ApiRecentCollection[] = [];
        if (Array.isArray(payload?.recentCollections)) {
            apiRecent = payload.recentCollections;
        } else if (recentCollectionRows.length > 0) {
            apiRecent = recentCollectionRows;
        } else if (Array.isArray(payload) && payload.length > 1 && Array.isArray(payload[1])) {
            apiRecent = payload[1];
        }

        let apiPending: ApiPendingCollection[] = [];
        if (Array.isArray(payload?.pendingCollections)) {
            apiPending = payload.pendingCollections;
        } else if (pendingCollectionRows.length > 0) {
            apiPending = pendingCollectionRows;
        } else if (Array.isArray(payload) && payload.length > 2 && Array.isArray(payload[2])) {
            apiPending = payload[2];
        }

        const uniqueDates = new Set<string>();

        const recentCollections: RecentCollectionRow[] = apiRecent.map((row, idx) => {
            const dateValue = row?.collectionDate as string | Date;
            const iso = toISODate(dateValue);
            if (iso) uniqueDates.add(iso);
            return {
                id: `rc-${idx}-${Date.now()}`,
                customer: row?.customerName ?? '',
                employee: row?.employeeName ?? '',
                amount: Number(row?.amount ?? 0),
                mode: row?.modeOfPayment ?? '',
                dateTime: formatDisplayDateTime(dateValue),
            };
        });

        const pendingCollections: PendingCollectionRow[] = apiPending.map((row, idx) => {
            const dateValue = row?.collectionDate as string | Date;
            return {
                id: `pc-${idx}-${Date.now()}`,
                companyName: row?.companyName ?? '',
                amount: Number(row?.amount ?? 0),
                pendingSync: row?.syncStatus ?? 'Pending',
                date: formatDisplayDate(dateValue),
            };
        });

        return {
            summary,
            recentCollections,
            pendingCollections,
            totalCollectionDays: uniqueDates.size,
        };
    };

    const fetchDashboardData = useCallback(async () => {
        try {
            setLoading(true);


            const resp: any = await commanServicer.fetchRequest(
                '',
                true,
                `AdminDashboard/getdashboarddata`,
                'GET'
            );

            if (resp?.statusCode === 401 || resp?.StatusCode === 401) {
                customToast.error('Session expired, please login again');
                setData(emptyData);
                return;
            }

            if (resp?.statusCode === 400 || resp?.StatusCode === 400) {
                customToast.error(resp?.message || resp?.Message || 'Bad request');
                setData(emptyData);
                return;
            }

            const apiStatus = resp?.status ?? resp?.Status;
            const responseData = resp?.data ?? resp?.Data ?? {};

            if (apiStatus === true) {
                setData(transformApiData(responseData));
            } else {
                customToast.Warning(resp?.message || resp?.Message || 'No data found');
                setData(emptyData);
            }
        } catch (ex: any) {
            console.error('Dashboard fetch failed', ex);
            customToast.error(ex?.message || 'Failed to load dashboard data');
            setData(emptyData);
        } finally {
            setFetchedAtLeastOnce(true);
            setTimeout(() => setLoading(false), 300);
        }
    }, [defaultDates.dateFrom, defaultDates.dateTo, setLoading]);

    useEffect(() => {
        fetchDashboardData();
    }, [fetchDashboardData]);


    const goToCollectionSummary = () => {
        navigate(`/Report/view/${PageEnums.CollectionSummeryReport}`);
    };

    const goToEmployeeMaster = () => {
        navigate(`/Report/view/${PageEnums.UserList}`);
    };

    const goToCollectionTransaction = () => {
        navigate(`/Report/view/${PageEnums.CashCollectionApproval}`);
    }

    const goToPendingCollectionReport = () => {
        navigate(`/Report/view/${PageEnums.PendingCollectionReport}`);
    }

    return (
        <div className="home-page-wrap">
            <div className="shadow">
                <div className="bg-line">
                    <span className="bg-head"><FaTachometerAlt /> &nbsp;Dashboard</span>
                </div>
                <hr className="colorgraph" />
            </div>

            <div className="home-greeting-section">
                <div className="home-greeting-row">
                    <div>
                        <h2 className="home-greeting">Good morning, Admin</h2>
                        <p className="home-greeting-sub">Here's how collections are trending this period</p>
                    </div>
                </div>
            </div>

            <div className="home-stats-row">
                <StatCard
                    icon={<FiTrendingUp />}
                    title="TOTAL COLLECTION"
                    value={formatRupees(data.summary.totalCollection)}
                    subtitle={`↑ across ${data.totalCollectionDays} collection days`}
                    subtitleColor="green"
                    clickable={true}
                    onClick={goToCollectionSummary}
                />
                <StatCard
                    icon={<FiUsers />}
                    title="FIELD EMPLOYEES"
                    value={`${data.summary.activeEmployees} / ${data.summary.totalEmployees}`}
                    subtitle={`${data.summary.inactiveEmployees} inactive`}
                    subtitleColor="green"
                    clickable={true}
                    onClick={goToEmployeeMaster}
                />
                <StatCard
                    icon={<FiClock />}
                    title="CASH APPROVAL PENDING"
                    value={formatRupees(data.summary.cashApprovalPendingAmount)}
                    subtitle="View report →"
                    subtitleColor="gold"
                    clickable={true}
                    onClick={goToCollectionTransaction}
                />
                <StatCard
                    icon={<FiAlertCircle />}
                    title="SYNC Fail AMOUNT"
                    value={formatRupees(data.summary.pendingSyncCollectionAmount)}
                    subtitle="View report →"
                    subtitleColor="gold"
                    clickable={true}
                    onClick={goToPendingCollectionReport}
                />
                <StatCard
                    icon={<FiRefreshCw />}
                    title="SYNC Fail COUNT"
                    value={String(data.summary.pendingSyncCount)}
                    subtitle="View report →"
                    subtitleColor="gold"
                    clickable={true}
                    onClick={goToPendingCollectionReport}
                />
            </div>

            <div className="home-tables-grid">
                <div className="home-table-card card-container">
                    <div className="home-table-head">
                        <div className="home-table-title-wrap">
                            <h3 className="home-table-title">Recent Collection</h3>
                        </div>
                        <div className="home-table-actions">
                            <button className="btn1" onClick={goToCollectionSummary}>
                                View Collection Summary
                                <FaChevronRight />
                            </button>
                        </div>
                    </div>
                    <div className="datatable1 home-datatable">
                        <table>
                            <thead>
                                <tr>
                                    <th>CUSTOMER</th>
                                    <th>EMPLOYEE</th>
                                    <th>AMOUNT</th>
                                    <th>MODE</th>
                                    <th>DATE & TIME</th>
                                </tr>
                            </thead>
                            <tbody>
                                {!fetchedAtLeastOnce ? (
                                    <tr>
                                        <td colSpan={5} className="home-empty-row">Loading data...</td>
                                    </tr>
                                ) : data.recentCollections.length === 0 ? (
                                    <tr>
                                        <td colSpan={5} className="home-empty-row">No recent collections available</td>
                                    </tr>
                                ) : (
                                    data.recentCollections.map((row) => (
                                        <tr key={row.id}>
                                            <td>{row.customer}</td>
                                            <td>{row.employee}</td>
                                            <td className="home-amount-cell">{formatRupees(row.amount).replace('₹ ', '₹')}</td>
                                            <td>{row.mode}</td>
                                            <td>{row.dateTime}</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="home-table-card card-container">
                    <div className="home-table-head">
                        <div className="home-table-title-wrap">
                            <h3 className="home-table-title">Pending Sync Collection</h3>
                        </div>
                        <div className="home-table-actions">
                            <button className="btn1" onClick={goToPendingCollectionReport}>
                                View full report
                                <FaChevronRight />
                            </button>
                        </div>
                    </div>
                    <div className="datatable1 home-datatable">
                        <table>
                            <thead>
                                <tr>
                                    <th>COMPANY NAME</th>
                                    <th>DATE</th>
                                    <th>AMOUNT</th>
                                    <th>PENDING SYNC</th>
                                </tr>
                            </thead>
                            <tbody>
                                {!fetchedAtLeastOnce ? (
                                    <tr>
                                        <td colSpan={4} className="home-empty-row">Loading data...</td>
                                    </tr>
                                ) : data.pendingCollections.length === 0 ? (
                                    <tr>
                                        <td colSpan={4} className="home-empty-row">No pending collections available</td>
                                    </tr>
                                ) : (
                                    data.pendingCollections.map((row) => (
                                        <tr key={row.id}>
                                            <td>{row.companyName}</td>
                                            <td>{row.date}</td>
                                            <td className="home-amount-cell">{formatRupees(row.amount).replace('₹ ', '₹')}</td>
                                            <td>
                                                <span className="home-pending-tag">{row.pendingSync}</span>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DashboardHome;
